// src/context/UserContext.tsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { axiosRequest } from '../service/AxiosService';

// ユーザー情報の型定義
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profiles?: string[];
  groups?: string[];
  authorities?: string[];
  // 必要に応じて他の情報を追加
}

/**
 * ユーザーコンテキストの型定義
 */
interface UserContextType {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  fetchUser: () => Promise<void>; // ユーザー情報を手動で再取得できるようにする
}
// UserProviderの型定義
interface UserProviderProps {
  children: React.ReactNode;  // children プロパティを追加
}
const UserContext = createContext<UserContextType | undefined>(undefined);

// ユーザー情報を取得する関数
/**
 * サーバーからユーザー情報を取得する
 * @returns ユーザー情報、取得失敗時はnull
 */
const fetchUserInfo = async (): Promise<User | null> => {
  try {
    const response: User = await axiosRequest("/getUserInfo", "GET", {});

    // APIレスポンスの型が正しいかチェック（型ガード）
    if (response && response.id && response.firstName && response.lastName && response.email ) {
      return response as User;  // 型アサーションでレスポンスがUser型であることを保証
    }
  } catch (error) {
    console.error("ユーザー情報の取得に失敗しました:", error);
  }
  return null;
};

/**
 * ユーザー情報をアプリ全体に提供するコンテキストプロバイダー
 * - props: children（子コンポーネント）
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // ユーザー情報の取得
  const fetchUser = async () => {
    setLoading(true);
    const userInfo = await fetchUserInfo();
    setUser(userInfo);
    setLoading(false);
  };

  // 初回レンダリング時にユーザー情報を取得する
  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <UserContext.Provider value={{ user, loading, setUser, fetchUser }}>
      {loading ? <div>Loading...</div> : children}
    </UserContext.Provider>
  );
};

// useUser フックを作成して、コンテキストの値を簡単に利用できるようにする
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
